import { registerDecorators as _registerDecorators, LightningElement, registerComponent as _registerComponent } from "lwc";
import _tmpl from "./metadataCardV2.html";
import { PopoverHorizontalOrientation, PopoverVerticalOrientation } from 'wds/popover';
import { FORCE_COPILOT_LISTING_TYPE } from '~/constants';
import { FeatureFlags } from '~/modules/feature_flags';
import { Routes } from '~/modules/routes';
import { i18nStore, WireI18n } from '~/modules/stores/i18nStore';
import { navigate } from '~/modules/stores/routerStore';
import { getCurrentVersion, isConsultantListing, isPluginListing, isSkillListing, isSupportedTechnologyType, selectExtensionListing } from '~/modules/utilities/listing_utils';
import { getPluginLastModifiedDate, getPluginPublishedDate } from '~/modules/utilities/plugin_utils';
import { upsertQueryParams } from '~/modules/utilities/router';
import { getSkillLastModifiedDate, getSkillPublishedDate, getSkillWorksWithInstallMethodIds } from '~/modules/utilities/skill_utils';
import { printDateSimple } from '~/modules/utilities/time';
/** Reviews tab code — kept in sync with `pages/listingDetailV2`'s REVIEWS_TAB_CODE. */
const REVIEWS_TAB_CODE = 'r';

// Match `wds/listingCard`'s SKILL_BADGE_ICON / PLUGIN_BADGE_ICON, so the Solution Type row's
// glyph is identical to the shared skill/plugin badge.
const SKILL_TYPE_ICON = 'work_order_type';
const PLUGIN_TYPE_ICON = 'apex_plugin';

/** Number of stars in the rating display (Figma 7764:134862 renders five). */
const NUMBER_OF_STARS = 5;

/** One favorite-star in the rating row, with a 0–100 fill gradient. */

/**
 * v2 side-panel Metadata card — embedded by `pages/listingDetailV2` in the
 * sticky left panel (Figma 7764:135352 "Metadata").
 *
 * Extracted verbatim from `listingDetailV2` so the card is a self-contained,
 * `listing`-driven unit rather than ~20 getters + ~110 lines of markup living
 * on the page. Behaviour is unchanged: same `data-testid`s, same rows, same
 * consultant handling. Version/date logic mirrors the former `listing/appDetails`
 * (the v1 panel that rendered the same fields inside the Specs tab); cert stats
 * mirror the former `listing/titledGroups`.
 *
 * Follows the V2 fork convention (`listing/overviewV2`): a standalone component
 * taking `@api listing`, not a subclass of any v1 component. No `.js-meta.xml`
 * is needed — the page embeds it directly as `<listing-metadata-card-v2>`.
 */
class MetadataCardV2 extends LightningElement {
  constructor(...args) {
    super(...args);
    this.listing = void 0;
    this.i18n = void 0;
  }
  get isConsultantListing() {
    return isConsultantListing(this.listing);
  }
  get isSkillListing() {
    return isSkillListing(this.listing);
  }
  get isPluginListing() {
    return isPluginListing(this.listing);
  }
  get averageRating() {
    return this.listing?.reviewsSummary?.averageRating || 0;
  }
  get reviewsCount() {
    return this.listing?.reviewsSummary?.reviewCount || 0;
  }
  get extensionListing() {
    return selectExtensionListing(this.listing);
  }
  get isSupportedTechnologyType() {
    return isSupportedTechnologyType(this.listing);
  }
  get shouldShowSolutionTypeBadge() {
    return this.isSupportedTechnologyType && !this.isConsultantListing;
  }
  get solutionType() {
    if (this.extensionListing?.appType === 'Package/Platform/Data') {
      return this.i18n.search.filters.solutionType.lightningData;
    }
    return this.i18n.search.filters.solutionType[this.listing?.technology];
  }
  get solution() {
    return this.listing?.solution?.solution;
  }

  /** Publisher name for the metadata card (Figma shows the bare name, not the "by …" byline). */
  get metadataPublisherName() {
    return this.listing?.publisher?.name ?? '';
  }
  get currentVersion() {
    return getCurrentVersion(this.listing);
  }

  /**
   * Last Updated date. Skills and plugins have no `solution` (they carry no product package), so
   * their date comes from their own extension's `lastModifiedDate` instead — mirrors
   * `listing/skillSidebar`'s `lastUpdated` getter.
   */
  get lastUpdated() {
    if (this.isSkillListing) {
      return printDateSimple(getSkillLastModifiedDate(this.listing));
    }
    if (this.isPluginListing) {
      return printDateSimple(getPluginLastModifiedDate(this.listing));
    }
    return printDateSimple(this.solution?.lastModifiedDate);
  }

  /**
   * Listed On date. Skills and plugins read `publishedDate` off their own extension rather than
   * `extensionListing.publishedDate` — mirrors `listing/skillSidebar`'s `listedOn` getter.
   */
  get listedOn() {
    if (this.isSkillListing) {
      return printDateSimple(getSkillPublishedDate(this.listing));
    }
    if (this.isPluginListing) {
      return printDateSimple(getPluginPublishedDate(this.listing));
    }
    return printDateSimple(this.extensionListing?.publishedDate);
  }

  /**
   * Solution-type value for the metadata card. Consultants surface the shared
   * "Salesforce Consultants" badge label (they have no `technology` mapping); skills surface the
   * shared "Skill" badge label; plugins surface the shared "Plugin" badge label (neither's
   * `technology`-derived `solutionType` has an i18n mapping); everything else falls back to the
   * technology-derived `solutionType`.
   */
  get solutionTypeLabel() {
    if (this.isConsultantListing) {
      return this.i18n.listingDetails.consultant.badge;
    }
    if (this.isSkillListing) {
      return this.i18n.skills.skillBadge;
    }
    if (this.isPluginListing) {
      return this.i18n.skills.pluginBadge;
    }
    return this.solutionType;
  }
  get shouldShowSolutionType() {
    // Consultants, skills, and plugins always show the row (with their shared badge label);
    // product listings show it when the technology maps to a badge.
    if (this.isConsultantListing || this.isSkillListing || this.isPluginListing) {
      return true;
    }
    return this.shouldShowSolutionTypeBadge && !!this.solutionType;
  }

  // --- Consultant certification stats ---------------------------------
  // Ported from the former `listing/titledGroups` so the metadata card surfaces the same
  // Certifications / Certified People stats (with their info tooltips) for
  // consultant listings, styled as metadata rows per Figma 7764:134651. Data
  // lives on `listing.consultantCertifications`.

  get certifiedPeopleCount() {
    return this.listing?.consultantCertifications?.certifiedExpertCount;
  }
  get totalCertificationsCount() {
    return this.listing?.consultantCertifications?.certificationCount;
  }

  // Both formatters are only rendered behind their `shouldShow…` (> 0) gates,
  // so `count` is always a positive number here; the `?? 0` is a type-safety
  // guard against `Intl.NumberFormat().format(undefined)` yielding "NaN".
  get formattedCertifiedPeopleCount() {
    return new Intl.NumberFormat().format(this.certifiedPeopleCount ?? 0);
  }
  get formattedTotalCertificationsCount() {
    return new Intl.NumberFormat().format(this.totalCertificationsCount ?? 0);
  }
  get shouldShowCertifiedPeople() {
    return (this.certifiedPeopleCount ?? 0) > 0;
  }
  get shouldShowCertifications() {
    return (this.totalCertificationsCount ?? 0) > 0;
  }
  get certifiedPeopleTooltip() {
    return this.i18n?.listingDetails?.certifiedPeopleTooltip;
  }
  get certificationsTooltip() {
    return this.i18n?.listingDetails?.certificationsTooltip;
  }

  // Center the tooltip arrow under the bare info icon (the default RIGHT
  // orientation leaves it off to the side). Pairs with
  // `--arrow-horizontal-center` in the css.
  get consultantStatTooltipOrientation() {
    return {
      vertical: PopoverVerticalOrientation.BOTTOM,
      horizontal: PopoverHorizontalOrientation.CENTER
    };
  }
  get shouldShowCurrentVersion() {
    // Skills carry no product version (they have their own, out-of-scope, skill version) — the
    // metadata card never shows a Current Version row for them, regardless of `currentVersion`.
    if (this.isSkillListing) {
      return false;
    }
    // Post-DF: Current Version is shown on the Security tab instead.
    if (FeatureFlags.FF_TRYBUY_LISTING_DETAIL_V2_POST_DF) {
      return false;
    }
    return !!this.currentVersion;
  }

  /**
   * Works With section for skills and plugins (Figma 2645:69251): one icon + name row per harness
   * the listing publishes in `supportedHarnesses`, using the same product labels as the skill
   * sidebar's "Install for" dropdown. Hidden until the listing carries that data.
   */
  get worksWithItems() {
    if (!this.isSkillListing && !this.isPluginListing) {
      return [];
    }
    const labels = {
      'agentforce-vibes': this.i18n?.skills?.installMethodAgentforceVibes,
      'claude-code': this.i18n?.skills?.installMethodClaude,
      cursor: this.i18n?.skills?.installMethodCursor,
      codex: this.i18n?.skills?.installMethodChatGpt,
      opencode: this.i18n?.skills?.installMethodOpenCode
    };
    return getSkillWorksWithInstallMethodIds(this.listing).map(id => ({
      id,
      label: labels[id] ?? ''
    })).filter(item => !!item.label);
  }
  get shouldShowWorksWith() {
    return this.worksWithItems.length > 0;
  }
  get shouldShowLastUpdated() {
    return !!this.lastUpdated;
  }
  get shouldShowListedOn() {
    return !!this.listedOn;
  }

  /** Skills and plugins have no reviews/ratings feature, so the whole Rating row is hidden. */
  get shouldShowRating() {
    return !this.isSkillListing && !this.isPluginListing;
  }

  /**
   * Whether the listing has any rating to show. When false the metadata row
   * still renders, falling back to a plain "No Ratings" value (see
   * `noRatingText`) styled like the card's other metadata values — matching
   * v1, which always surfaced the rating field.
   */
  get hasRating() {
    return this.averageRating > 0 || this.reviewsCount > 0;
  }

  /** "No Ratings" fallback, reusing the shared star-ratings label. */
  get noRatingText() {
    return this.i18n.starRatings.noReviews;
  }

  /**
   * Average rating rendered to two decimals (Figma 7764:134861 — the "4.6"
   * slot; two-decimal precision per product spec).
   */
  get formattedAverageRating() {
    return this.averageRating.toFixed(2);
  }

  /**
   * Five favorite-star icons with a per-star fill gradient (0–100), computed
   * exactly the way `wds/starRatings` fills its read-only stars: full stars
   * below the value fill 100%, the boundary star (`floor(rating)`) fills the
   * fractional remainder, the rest are empty. Kept byte-for-byte in step with
   * that widget's `starsArray` math so the stars read identically — we render
   * them inline only because Figma's layout (bold value · stars · clickable
   * count, no parentheses) doesn't match the widget's fixed "value (count)"
   * grouping.
   */
  get ratingStars() {
    const rating = this.averageRating;
    const ratingAsInteger = Math.floor(rating);
    const stars = [];
    for (let i = 0; i < NUMBER_OF_STARS; i++) {
      let gradient = 100;
      if (i >= rating) {
        gradient = 0;
      } else if (i === ratingAsInteger && rating % 1 !== 0) {
        gradient = Math.round(rating % 1 * 100);
      }
      stars.push({
        id: `rating-star-${i + 1}`,
        gradient
      });
    }
    return stars;
  }

  /** aria-label for the whole star group, e.g. "4.6 out of 5 stars". */
  get ratingAriaLabel() {
    return i18nStore.getState().i18n.starRatings.ariaLabelStar({
      CURRENT_STAR: this.averageRating,
      NUMBER_OF_STARS
    });
  }

  /**
   * Reviews-tab href for the count link. Same target as v1 / i18n
   * `heading.reviewsCount`. Rendered as a slotted `<a>` in a `wds-link` (see
   * the template) rather than an i18n-message so the anchor's underline can be
   * styled off directly; analytics `name="reviews"` still fires.
   */
  get reviewsTabHref() {
    return `${Routes.LISTING}?${upsertQueryParams(['tab', REVIEWS_TAB_CODE], ['scrollTo', 'reviews'])}`;
  }

  /**
   * The review-count link is a slotted `<a>` (not an i18n-message), so `wds-link`'s
   * built-in click interception doesn't reliably fire for it — without this the browser
   * follows the anchor's href and does a full-page reload. Intercept the click and do a
   * SPA navigation to the Reviews tab instead (matching the pricing link's approach).
   */
  handleReviewsLinkClick(event) {
    event.preventDefault();
    navigate(this.reviewsTabHref);
  }

  /**
   * Whether the Solution Type row shows the sprite technology icon (from `/images/icons.svg`).
   * Consultants render their own glyph instead (see `.solution-type-icon--consultant`); skills
   * and plugins have no technology-sprite mapping (the switch below would otherwise fall to its
   * 'salesforce' default) and their glyphs aren't in that sprite sheet, so they render their SLDS
   * badge glyph instead (see `solutionTypeGlyphIcon` / `.solution-type-glyph-icon`).
   */
  get shouldShowTechnologyIcon() {
    return !this.isConsultantListing && !this.isSkillListing && !this.isPluginListing;
  }

  /**
   * Solution Type row glyph for skills/plugins — same icon as their listing-card badge
   * (see `SKILL_TYPE_ICON` / `PLUGIN_TYPE_ICON` above). Every other listing type shows no icon
   * here (undefined), leaving the row as plain text.
   */
  get solutionTypeGlyphIcon() {
    if (this.isPluginListing) {
      return PLUGIN_TYPE_ICON;
    }
    if (this.isSkillListing) {
      return SKILL_TYPE_ICON;
    }
    return undefined;
  }
  get iconTechnologySymbol() {
    switch (this.listing?.technology) {
      case 'force/package':
      case 'force/bolt':
      case 'force/flow':
      case 'force/lightning':
      case 'salesforce-platform/api':
        return 'salesforce';
      case 'b2c-commerce/package':
      case 'b2c-commerce/api':
        // b2c-v2 is the whitespace-cropped variant of the v1 `b2c`
        // sprite symbol; v1 (listingDetail.ts) still uses `b2c`.
        return 'b2c-v2';
      case 'tableau/accelerator':
        return 'tableau';
      case 'quip/package':
        return 'quip';
      case 'marketing-cloud/api':
        return 'marketing-cloud';
      case FORCE_COPILOT_LISTING_TYPE:
        return 'agentforce';
      default:
        return 'salesforce';
    }
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(MetadataCardV2, {
  publicProps: {
    listing: {
      config: 0
    }
  },
  wire: {
    i18n: {
      adapter: WireI18n,
      config: function ($cmp) {
        return {};
      }
    }
  }
});
const __lwc_component_class_internal = _registerComponent(MetadataCardV2, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;